import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import { getDashboardStats, profitabilityAlerts, getCashflowChart, startInvoiceDunning, sendDunningNow } from '../../services/api';
import {
  Bell, AlertTriangle, Receipt, Briefcase, Activity, CalendarDays, X, ArrowRight, Sparkles, Send, FilePlus, Clock, Undo2, ChevronDown,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);

// Severity → visual style
const severityClass = {
  critical: 'bg-red-50 border-red-200 text-red-700',
  warning: 'bg-amber-50 border-amber-200 text-amber-700',
  info: 'bg-sky-50 border-sky-200 text-sky-700',
};

const TYPE_META = {
  overdue: { icon: Receipt, label: 'Facturas vencidas', color: 'text-red-600 bg-red-50' },
  cashflow: { icon: Activity, label: 'Cashflow', color: 'text-rose-600 bg-rose-50' },
  project: { icon: Briefcase, label: 'Proyectos en riesgo', color: 'text-amber-600 bg-amber-50' },
  tax: { icon: CalendarDays, label: 'Impuestos próximos', color: 'text-violet-600 bg-violet-50' },
};

const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSeen, setLastSeen] = useState(() => {
    try { return parseInt(localStorage.getItem('notif:lastSeen') || '0', 10); } catch { return 0; }
  });
  const [snoozes, setSnoozes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('notif:snoozes') || '{}'); } catch { return {}; }
  });
  const [showSnoozed, setShowSnoozed] = useState(false);
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);

  // Persist snoozes whenever they change and prune expired entries.
  useEffect(() => {
    const now = Date.now();
    const pruned = Object.fromEntries(Object.entries(snoozes).filter(([, until]) => until > now));
    try { localStorage.setItem('notif:snoozes', JSON.stringify(pruned)); } catch {}
    if (Object.keys(pruned).length !== Object.keys(snoozes).length) setSnoozes(pruned);
  }, [snoozes]);

  const snoozeItem = (e, item, days = 7) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    setSnoozes((s) => ({ ...s, [item.id]: until }));
    const labels = { 1: '1 día', 3: '3 días', 7: '7 días' };
    toast.success(`Aviso pospuesto ${labels[days] || `${days} días`}`);
  };

  // Snooze until next Monday morning (9am local)
  const snoozeUntilMonday = (e, item) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const d = new Date();
    const daysUntilMon = (1 + 7 - d.getDay()) % 7 || 7; // always next monday (never today)
    d.setDate(d.getDate() + daysUntilMon);
    d.setHours(9, 0, 0, 0);
    setSnoozes((s) => ({ ...s, [item.id]: d.getTime() }));
    toast.success(`Aviso pospuesto hasta el lunes`);
  };

  const [snoozeMenu, setSnoozeMenu] = useState(null); // currently open menu's item id

  const unsnoozeItem = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    setSnoozes((s) => {
      const next = { ...s };
      delete next[itemId];
      return next;
    });
    toast.success('Aviso reactivado');
  };

  const refresh = () => {
    setLoading(true);
    const list = [];
    Promise.allSettled([
      getDashboardStats(),
      profitabilityAlerts(),
      getCashflowChart({ months: 6 }),
      api.get('/tax-forecast/current'),
    ]).then((results) => {
      // Overdue invoices
      const stats = results[0].status === 'fulfilled' ? results[0].value.data : null;
      (stats?.overdue_invoices || []).slice(0, 8).forEach((inv) => {
        list.push({
          id: `inv-${inv.id}`,
          type: 'overdue',
          severity: inv.days_overdue > 30 ? 'critical' : 'warning',
          title: `Factura ${inv.invoice_number} vencida hace ${inv.days_overdue} días`,
          subtitle: `${inv.client_name} · ${fmt(inv.total)}`,
          to: `/invoices/${inv.id}`,
          ts: Date.parse(inv.due_date || '') || 0,
          invoiceId: inv.id,
        });
      });
      // Profitability
      const prof = results[1].status === 'fulfilled' ? results[1].value.data?.items || [] : [];
      prof.slice(0, 5).forEach((a) => {
        list.push({
          id: `proj-${a.project_id}`,
          type: 'project',
          severity: a.severity === 'danger' ? 'critical' : 'warning',
          title: `Proyecto "${a.project_name}" en riesgo`,
          subtitle: `${a.client_name || ''} · Margen ${fmt(a.margin)}`,
          to: '/projects',
          ts: Date.now(),
          projectId: a.project_id,
          clientId: a.client_id,
        });
      });
      // Cashflow alerts
      const cf = results[2].status === 'fulfilled' ? results[2].value.data?.alerts || [] : [];
      cf.slice(0, 5).forEach((a, i) => {
        list.push({
          id: `cf-${a.month || i}`,
          type: 'cashflow',
          severity: 'critical',
          title: 'Saldo negativo proyectado',
          subtitle: a.message || `En ${a.month}`,
          to: '/cashflow',
          ts: Date.now(),
        });
      });
      // Tax upcoming (≤ 14 days)
      const tax = results[3].status === 'fulfilled' ? results[3].value.data?.upcoming || [] : [];
      tax.filter((t) => (t.days_to_due ?? 999) <= 14).forEach((t) => {
        list.push({
          id: `tax-${t.type}-${t.period_year}-${t.period_quarter || ''}`,
          type: 'tax',
          severity: t.days_to_due <= 7 ? 'critical' : 'warning',
          title: `Modelo ${t.type} en ${t.days_to_due} días`,
          subtitle: `${t.label} · ${fmt(t.projected_at_period_end)}`,
          to: '/tax/forecast',
          ts: Date.parse(t.due_date || '') || 0,
        });
      });

      // Sort critical first then by recency
      list.sort((a, b) => {
        const sev = { critical: 0, warning: 1, info: 2 };
        if (sev[a.severity] !== sev[b.severity]) return sev[a.severity] - sev[b.severity];
        return (b.ts || 0) - (a.ts || 0);
      });
      setItems(list);
      setLoading(false);
    });
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      // Close snooze submenu first if clicking outside any submenu
      if (snoozeMenu && !e.target.closest('[data-testid^="snooze-menu-"]') && !e.target.closest('[data-testid^="quick-snooze-menu-"]')) {
        setSnoozeMenu(null);
      }
      const inWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inWrapper && !inPanel) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [snoozeMenu]);

  // Split items into active vs snoozed based on stored snoozes
  const now = Date.now();
  const activeItems = items.filter((i) => !snoozes[i.id] || snoozes[i.id] <= now);
  const snoozedItems = items.filter((i) => snoozes[i.id] && snoozes[i.id] > now);

  const unread = activeItems.filter((i) => (i.ts || Date.now()) > lastSeen).length;
  const totalCount = activeItems.length;
  const snoozedCount = snoozedItems.length;

  const markAllSeen = () => {
    const now = Date.now();
    setLastSeen(now);
    try { localStorage.setItem('notif:lastSeen', String(now)); } catch {}
  };

  // ---- Quick actions ---------------------------------------------------
  const [busy, setBusy] = useState({}); // {itemId: true}

  const sendInvoiceReminder = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy[item.id]) return;
    setBusy((b) => ({ ...b, [item.id]: true }));
    try {
      // Start the dunning sequence (idempotent) then send the first step now.
      try { await startInvoiceDunning(item.invoiceId); } catch (_) {}
      await sendDunningNow(item.invoiceId, { step_index: 0 });
      toast.success('Recordatorio enviado al cliente');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo enviar el recordatorio';
      toast.error(typeof msg === 'string' ? msg : 'Error al enviar recordatorio');
    } finally {
      setBusy((b) => ({ ...b, [item.id]: false }));
    }
  };

  const visibleItems = showSnoozed ? snoozedItems : activeItems;
  const groups = visibleItems.reduce((acc, it) => {
    acc[it.type] = acc[it.type] || [];
    acc[it.type].push(it);
    return acc;
  }, {});

  return (
    <div ref={wrapperRef} className="relative" data-testid="notification-center">
      <button
        onClick={() => { setOpen(!open); if (!open) markAllSeen(); }}
        className="relative p-2 hover:bg-stone-100 rounded-lg transition-colors"
        title="Notificaciones"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4 text-stone-500" />
        {totalCount > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-extrabold text-white flex items-center justify-center ${unread > 0 ? 'bg-[#FF6B4A] animate-pulse' : 'bg-stone-400'}`}
            data-testid="notification-badge"
          >
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed bottom-2 left-2 right-2 lg:bottom-6 lg:left-[244px] lg:right-auto lg:w-[360px] max-h-[480px] bg-white rounded-2xl shadow-[0_20px_50px_rgb(0,0,0,0.18)] border border-stone-200 overflow-hidden z-[100] flex flex-col"
          data-testid="notification-panel"
        >
          <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] flex items-center justify-center">
                <Bell className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-sm font-extrabold text-stone-900 font-heading">Centro de alertas</p>
                <p className="text-[10px] text-stone-400">{totalCount} activo{totalCount === 1 ? '' : 's'}{snoozedCount > 0 ? ` · ${snoozedCount} pospuesto${snoozedCount === 1 ? '' : 's'}` : ''}</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-stone-100 rounded-md" data-testid="notification-close">
              <X className="w-4 h-4 text-stone-400" />
            </button>
          </div>

          {/* Tabs: Activos / Pospuestos */}
          {(snoozedCount > 0 || showSnoozed) && (
            <div className="px-4 pt-2 pb-2 flex items-center gap-1 bg-stone-50/40 border-b border-stone-100">
              <button
                onClick={() => setShowSnoozed(false)}
                className={`text-[11px] font-bold px-3 py-1.5 rounded-full transition-all ${!showSnoozed ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                data-testid="notif-tab-active"
              >
                Activos ({totalCount})
              </button>
              <button
                onClick={() => setShowSnoozed(true)}
                className={`inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full transition-all ${showSnoozed ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                data-testid="notif-tab-snoozed"
              >
                <Clock className="w-3 h-3" />
                Pospuestos ({snoozedCount})
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-stone-400">Cargando avisos…</div>
            ) : visibleItems.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-stone-400">
                <Sparkles className="w-8 h-8 mx-auto mb-2 text-emerald-300" />
                <p className="font-bold text-stone-600">{showSnoozed ? 'Sin avisos pospuestos' : 'Todo está en orden'}</p>
                <p className="text-xs mt-1">{showSnoozed ? 'Cuando pospongas un aviso, aparecerá aquí.' : 'No tienes alertas pendientes 🎉'}</p>
              </div>
            ) : (
              Object.entries(groups).map(([type, list]) => {
                const meta = TYPE_META[type] || TYPE_META.overdue;
                const Icon = meta.icon;
                return (
                  <div key={type} className="border-b border-stone-100 last:border-0" data-testid={`notif-group-${type}`}>
                    <div className="px-4 py-2 flex items-center gap-2 sticky top-0 bg-white/95 backdrop-blur-sm">
                      <div className={`w-5 h-5 rounded-md ${meta.color} flex items-center justify-center`}>
                        <Icon className="w-3 h-3" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{meta.label}</span>
                      <span className="ml-auto text-[10px] font-bold text-stone-400">{list.length}</span>
                    </div>
                    {list.slice(0, 6).map((n) => (
                      <div
                        key={n.id}
                        className="px-4 py-2.5 hover:bg-stone-50 transition-colors group"
                        data-testid={`notif-item-${n.id}`}
                      >
                        <Link to={n.to} onClick={() => setOpen(false)} className="block">
                          <div className="flex items-start gap-2">
                            <span className={`mt-1 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${n.severity === 'critical' ? 'bg-red-500' : n.severity === 'warning' ? 'bg-amber-500' : 'bg-sky-500'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold text-stone-800 truncate">{n.title}</p>
                              {n.subtitle && <p className="text-[11px] text-stone-500 truncate mt-0.5">{n.subtitle}</p>}
                            </div>
                            <ArrowRight className="w-3 h-3 text-stone-300 group-hover:text-[#FF6B4A] flex-shrink-0 mt-1" />
                          </div>
                        </Link>
                        {/* Quick actions */}
                        <div className="pl-3.5 mt-1.5 flex items-center gap-2 flex-wrap">
                          {n.type === 'overdue' && n.invoiceId && !showSnoozed && (
                            <button
                              onClick={(e) => sendInvoiceReminder(e, n)}
                              disabled={!!busy[n.id]}
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded-full transition-all"
                              data-testid={`quick-remind-${n.invoiceId}`}
                            >
                              <Send className="w-2.5 h-2.5" />
                              {busy[n.id] ? 'Enviando…' : 'Enviar recordatorio'}
                            </button>
                          )}
                          {n.type === 'project' && n.clientId && !showSnoozed && (
                            <Link
                              to={`/invoices/new?client_id=${n.clientId}${n.projectId ? `&project_id=${n.projectId}` : ''}`}
                              onClick={() => setOpen(false)}
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-full transition-all"
                              data-testid={`quick-invoice-${n.projectId}`}
                            >
                              <FilePlus className="w-2.5 h-2.5" />
                              Crear factura
                            </Link>
                          )}
                          {!showSnoozed ? (
                            <div className="relative inline-flex">
                              <button
                                onClick={(e) => snoozeItem(e, n, 7)}
                                className="inline-flex items-center gap-1 text-[10px] font-bold text-stone-500 bg-stone-100 hover:bg-stone-200 pl-2 pr-1.5 py-1 rounded-l-full transition-all border-r border-stone-200/80"
                                data-testid={`quick-snooze-${n.id}`}
                                title="Posponer este aviso 7 días"
                              >
                                <Clock className="w-2.5 h-2.5" />
                                Posponer 7d
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSnoozeMenu(snoozeMenu === n.id ? null : n.id);
                                }}
                                className="inline-flex items-center text-stone-500 bg-stone-100 hover:bg-stone-200 px-1.5 py-1 rounded-r-full transition-all"
                                data-testid={`quick-snooze-menu-${n.id}`}
                                title="Más opciones"
                              >
                                <ChevronDown className="w-2.5 h-2.5" />
                              </button>
                              {snoozeMenu === n.id && (
                                <div
                                  className="absolute top-full mt-1 left-0 z-10 bg-white rounded-xl shadow-[0_8px_24px_rgb(0,0,0,0.12)] border border-stone-200 min-w-[150px] py-1"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  data-testid={`snooze-menu-${n.id}`}
                                >
                                  {[
                                    { days: 1, label: 'Mañana' },
                                    { days: 3, label: 'En 3 días' },
                                    { days: 7, label: 'En 1 semana' },
                                  ].map((opt) => (
                                    <button
                                      key={opt.days}
                                      onClick={(e) => { snoozeItem(e, n, opt.days); setSnoozeMenu(null); }}
                                      className="w-full text-left px-3 py-1.5 text-[11px] font-medium text-stone-700 hover:bg-stone-50 flex items-center justify-between"
                                      data-testid={`snooze-opt-${opt.days}d-${n.id}`}
                                    >
                                      <span>{opt.label}</span>
                                      <span className="text-stone-400 text-[10px]">{opt.days}d</span>
                                    </button>
                                  ))}
                                  <div className="border-t border-stone-100 my-1" />
                                  <button
                                    onClick={(e) => { snoozeUntilMonday(e, n); setSnoozeMenu(null); }}
                                    className="w-full text-left px-3 py-1.5 text-[11px] font-medium text-stone-700 hover:bg-stone-50"
                                    data-testid={`snooze-opt-monday-${n.id}`}
                                  >
                                    Hasta el lunes
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={(e) => unsnoozeItem(e, n.id)}
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 px-2 py-1 rounded-full transition-all"
                              data-testid={`quick-unsnooze-${n.id}`}
                            >
                              <Undo2 className="w-2.5 h-2.5" />
                              Reactivar
                              <span className="text-stone-400 ml-1">
                                · vuelve {new Date(snoozes[n.id]).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>

          {(activeItems.length > 0 || snoozedCount > 0) && (
            <div className="px-4 py-2 border-t border-stone-100 bg-stone-50/60 flex items-center justify-between">
              <button
                onClick={refresh}
                className="text-[10px] font-bold uppercase tracking-widest text-stone-500 hover:text-[#FF6B4A] transition-colors"
                data-testid="notification-refresh"
              >
                Refrescar
              </button>
              {!showSnoozed && snoozedCount > 0 && (
                <button
                  onClick={() => setShowSnoozed(true)}
                  className="text-[10px] font-bold uppercase tracking-widest text-violet-600 hover:text-violet-800 transition-colors inline-flex items-center gap-1"
                  data-testid="notification-view-snoozed"
                >
                  <Clock className="w-3 h-3" />
                  Pospuestos · {snoozedCount}
                </button>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationCenter;
